import type { Chart, ChartData } from 'chart.js';

export type TimeSeriesPeakPoint = {
  x: string;
  y: number;
  seriesIndex?: number;
};

const MIN_DELTA_RATIO_FOR_SERIES_SCALE = 0.2;
const MIN_PEAK_POINT_FEEDBACK_COUNT = 30;

type TimeSeriesDatasetCandidate = {
  data?: unknown[];
  meta?: {
    y?: unknown[];
  };
};

type TimeSeriesChartPointElement = {
  skip?: boolean;
};

type ParsedTimeSeriesPoint = {
  y?: unknown;
};

const getLabelString = (label: unknown): string | undefined => {
  if (typeof label === 'string') return label;
  if (typeof label === 'number') return String(label);
  return undefined;
};

const toTimeSeriesPeakPoint = (
  point: unknown,
  label: unknown,
): TimeSeriesPeakPoint | undefined => {
  if (isTimeSeriesPeakPoint(point)) return point;

  const x = getLabelString(label);
  if (!x || typeof point !== 'number' || !Number.isFinite(point)) {
    return undefined;
  }

  return {
    x,
    y: point,
  };
};

const getFiniteNumber = (value: unknown): number | undefined => {
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
};

const getParsedPointY = (parsed: unknown): number | undefined => {
  if (typeof parsed === 'number') return getFiniteNumber(parsed);
  if (!parsed || typeof parsed !== 'object') return undefined;

  return getFiniteNumber((parsed as ParsedTimeSeriesPoint).y);
};

export const isTimeSeriesPeakPoint = (
  point: unknown,
): point is TimeSeriesPeakPoint => {
  if (!point || typeof point !== 'object') return false;

  const candidate = point as Partial<TimeSeriesPeakPoint>;

  return (
    typeof candidate.x === 'string' &&
    typeof candidate.y === 'number' &&
    Number.isFinite(candidate.y)
  );
};

export const isSameTimeSeriesPeakPoint = (
  point: unknown,
  peakPoint?: TimeSeriesPeakPoint,
  label?: unknown,
  seriesIndex?: number,
) => {
  const candidate = toTimeSeriesPeakPoint(point, label);

  return (
    Boolean(peakPoint) &&
    Boolean(candidate) &&
    candidate?.x === peakPoint?.x &&
    candidate?.y === peakPoint?.y &&
    (peakPoint?.seriesIndex == null || peakPoint.seriesIndex === seriesIndex)
  );
};

export const getTimeSeriesPeakPointCandidates = (
  data: ChartData<'line'>,
): TimeSeriesPeakPoint[] => {
  return data.datasets.flatMap((dataset, seriesIndex) => {
    const candidate = dataset as TimeSeriesDatasetCandidate;

    return (candidate.data ?? []).flatMap((point, index) => {
      const label = candidate.meta?.y?.[index] ?? data.labels?.[index];
      const peakPoint = toTimeSeriesPeakPoint(point, label);
      return peakPoint ? [{ ...peakPoint, seriesIndex }] : [];
    });
  });
};

export const getRenderedTimeSeriesPeakPointCandidates = (
  chart: Chart<'line'>,
): TimeSeriesPeakPoint[] => {
  return chart.data.datasets.flatMap((dataset, seriesIndex) => {
    if (!chart.isDatasetVisible(seriesIndex)) return [];

    const candidate = dataset as TimeSeriesDatasetCandidate;
    const meta = chart.getDatasetMeta(seriesIndex);

    return (meta._parsed ?? []).flatMap((parsedPoint, index) => {
      const element = meta.data[index] as
        | TimeSeriesChartPointElement
        | undefined;

      if (element?.skip) return [];

      const label = candidate.meta?.y?.[index] ?? chart.data.labels?.[index];
      const rawPeakPoint = toTimeSeriesPeakPoint(
        candidate.data?.[index],
        label,
      );
      const x = rawPeakPoint?.x ?? getLabelString(label);
      const y = getParsedPointY(parsedPoint) ?? rawPeakPoint?.y;

      return x && y != null ? [{ x, y, seriesIndex }] : [];
    });
  });
};

const getSeriesIndex = (point: TimeSeriesPeakPoint) => {
  return point.seriesIndex ?? 0;
};

const getDelta = (
  previousPoint: TimeSeriesPeakPoint,
  point: TimeSeriesPeakPoint,
) => {
  return point.y - previousPoint.y;
};

const getSeriesScaleThreshold = (points: TimeSeriesPeakPoint[]) => {
  const maxValue = points.reduce(
    (max, point) => Math.max(max, Math.abs(point.y)),
    0,
  );

  return maxValue * MIN_DELTA_RATIO_FOR_SERIES_SCALE;
};

const normalizeDelta = (delta: number, threshold: number) => {
  return Math.abs(delta) < threshold ? 0 : delta;
};

const isProcessablePeakPoint = (point: TimeSeriesPeakPoint) => {
  return point.y >= MIN_PEAK_POINT_FEEDBACK_COUNT;
};

const pickBestTimeSeriesPeakPoint = (
  points: TimeSeriesPeakPoint[],
  threshold = getSeriesScaleThreshold(points),
): TimeSeriesPeakPoint | undefined => {
  let bestCandidate:
    | {
        point: TimeSeriesPeakPoint;
        delta: number;
      }
    | undefined;
  for (let index = 1; index < points.length; index += 1) {
    const previousPoint = points[index - 1];
    const point = points[index];

    if (getSeriesIndex(previousPoint) !== getSeriesIndex(point)) {
      continue;
    }

    const delta = normalizeDelta(getDelta(previousPoint, point), threshold);

    if (delta <= 0) {
      continue;
    }

    if (!isProcessablePeakPoint(point)) {
      continue;
    }

    if (!bestCandidate) {
      bestCandidate = { point, delta };
      continue;
    }

    if (delta > bestCandidate.delta) {
      bestCandidate = { point, delta };
    }
  }

  return bestCandidate?.point;
};

export const pickTimeSeriesPeakPoint = (
  points: TimeSeriesPeakPoint[],
): TimeSeriesPeakPoint | undefined => {
  return pickBestTimeSeriesPeakPoint(points);
};

export const pickTimeSeriesPeakPoints = (
  points: TimeSeriesPeakPoint[],
): TimeSeriesPeakPoint[] => {
  const pointsBySeries = new Map<number, TimeSeriesPeakPoint[]>();
  const threshold = getSeriesScaleThreshold(points);

  for (const point of points) {
    const seriesIndex = getSeriesIndex(point);
    pointsBySeries.set(seriesIndex, [
      ...(pointsBySeries.get(seriesIndex) ?? []),
      point,
    ]);
  }

  return Array.from(pointsBySeries.values()).flatMap((seriesPoints) => {
    const peakPoint = pickBestTimeSeriesPeakPoint(seriesPoints, threshold);
    return peakPoint ? [peakPoint] : [];
  });
};
